import React from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "src/store";
import { HoverRow } from "src/ui/shared/table/HoverRow";
import { MinimalTableCell } from "src/ui/shared/table/MinimalTableComponents";
import { makeStyles } from "tss-react/mui";

import { selectGroupDetails, showDetails } from "@dashboard/devices/store";
import { alpha } from "@mui/material";
import ElasticService from "@services/elasticService";
import { DragHandle } from "@shared/DragHandle";
import { TableCellContent } from "@shared/table/TableRowCells";
import { Cell, Row } from "@tanstack/react-table";

import { GroupOverviewData } from "../GroupPage.utils";

import { useGroupRowDrag } from "./table/useGroupDrag";

const ignoredColumns = ["name", "action", "numberOfDevices"];

const tint = (color: string, opacity: number) => `linear-gradient(${alpha(color, opacity)}, ${alpha(color, opacity)})`;

const useStyles = makeStyles()((theme) => ({
    dragging: { opacity: 0.4 },
    dropAllowed: { backgroundImage: tint(theme.palette.emteria.primary, 0.14) },
    dropNotAllowed: {
        backgroundImage: tint(theme.palette.emteria.error, 0.08),
        outline: `1px dashed ${theme.palette.emteria.error}`,
        outlineOffset: -1,
    },
    nameCell: { position: "relative" },
    handle: {
        position: "absolute",
        left: theme.spacing(0.5),
        top: "50%",
        transform: "translateY(-50%)",
    },
}));

export const GroupTableRow = (props: Row<GroupOverviewData>) => {
    const { t } = useTranslation(["pages"]);
    const { classes, cx } = useStyles();
    const dispatch = useDispatch();
    // Subscribe to just the selected boolean, not the whole details object: any details change
    // (selecting another row, the resetDetails fired on group create) would otherwise re-render
    // every row. Now only the rows whose selected state actually flips re-render.
    const isSelected = useSelector((state) => selectGroupDetails(state)?.selectedId === props.original.id);
    const { rowRef, handleRef, onHandlePointerEnter, isDragging, dropState } = useGroupRowDrag(
        props.original.id,
        props.getCanExpand() && !props.getIsExpanded(),
    );

    const onClick = (cell: Cell<GroupOverviewData, unknown>) => {
        ElasticService.createTransaction("tableElementClick", {
            table: "groups",
            click_element: cell.column.id === "description" ? "link" : "text",
        });

        if (cell.row.original.id && !ignoredColumns.includes(cell.column.id)) {
            dispatch(
                showDetails({
                    type: "group",
                    selectedId: cell.row.original.id,
                }),
            );
        }
    };

    return (
        <HoverRow
            ref={rowRef}
            selected={isSelected}
            className={cx(
                isDragging && classes.dragging,
                dropState === "allowed" && classes.dropAllowed,
                dropState === "notAllowed" && classes.dropNotAllowed,
            )}
        >
            {props.getVisibleCells().map((cell) => (
                <MinimalTableCell
                    key={cell.id}
                    onClick={() => onClick(cell)}
                    className={cell.column.id === "name" ? classes.nameCell : undefined}
                    fluid={cell.column.columnDef.meta?.fluid}
                    fluidMinWidth={cell.column.columnDef.meta?.fluidMinWidth}
                >
                    {cell.column.id === "name" && handleRef ? (
                        <DragHandle
                            ref={handleRef}
                            className={classes.handle}
                            title={t("pages:groupPage.tooltip.dragToMove")}
                            onPointerEnter={onHandlePointerEnter}
                        />
                    ) : null}
                    <TableCellContent cell={cell} />
                </MinimalTableCell>
            ))}
        </HoverRow>
    );
};
